import { InfoCircleOutlined } from '@ant-design/icons';
import { Radio, Tooltip } from 'antd';
import { MeasurementUnitEnum } from 'domains/measurementDomain';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { useTranslation } from 'react-i18next';
import './index.less';

type Props = {
  /** Set false on a narrow header where the word "Units" does not fit. */
  withLabel?: boolean;
  /** Set false to drop the explanatory tooltip. */
  withHint?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
};

/**
 * Picks the unit a form is filled in and submitted in.
 *
 * Put one of these on a form that takes measurements, somewhere that governs the whole form - the
 * page header of a single-page form, the wizard header of a multi-step one. A wizard gets exactly
 * one: asking again on each of eight steps is both more work and an invitation to answer it
 * differently on step 5 than on step 3.
 *
 * It only does anything under a `MeasurementUnitScope`; outside one it would switch the reader's
 * own setting, which is the profile menu's job and not something a form should reach out and do.
 *
 * Switching re-expresses what is already in the form rather than relabelling it - see
 * `useRefillOnUnitSwitch` and `useCustomSizeUnit`. A number whose unit just changed cannot
 * honestly be left alone, and leaving it is what wrote `8.5x11` into the database as millimetres.
 */
export const RevxUnitSelect = ({
  withLabel = true,
  withHint = true,
  size = 'small',
  className,
}: Props) => {
  const { t } = useTranslation();
  const { unit, switchUnit, scoped } = useMeasurementUnit();

  return (
    <span className={`revx-unit-select ${className ?? ''}`}>
      {withLabel && (
        <span className="revx-unit-select-label">
          {t('common.measurementUnitLabel')}
        </span>
      )}
      <Radio.Group
        value={unit}
        size={size}
        optionType="button"
        buttonStyle="solid"
        onChange={(e) => switchUnit(e.target.value as MeasurementUnitEnum)}
      >
        <Radio.Button value={MeasurementUnitEnum.IMPERIAL}>
          {t('common.measurementUnitOptionImperial')}
        </Radio.Button>
        <Radio.Button value={MeasurementUnitEnum.METRIC}>
          {t('common.measurementUnitOptionMetric')}
        </Radio.Button>
      </Radio.Group>
      {withHint && (
        <Tooltip
          title={t(
            scoped
              ? 'common.measurementUnitHint'
              : 'common.measurementUnitGlobalHint'
          )}
        >
          <InfoCircleOutlined className="revx-unit-select-hint" />
        </Tooltip>
      )}
    </span>
  );
};

export default RevxUnitSelect;
